import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Rocket, Github, Presentation, PlayCircle, Share2, HeartHandshake, MessageCircleHeart, ExternalLink, Twitter, Linkedin, Instagram, Globe, Star } from "lucide-react";
import { toast } from "sonner";
import Shell from "../components/Shell";
import { HeroArt, TrackBadge, TagChip, Avatar } from "../components/bits";
import { WinnerBadge } from "../components/winners/WinnerBadge";
import SupportSheet from "../components/SupportSheet";
import { UBot } from "../components/UBot";
import { useApp } from "../context/AppContext";
import { SUPPORT_ENABLED } from "../lib/features";
import { endpoints, API } from "../lib/api";
import { cardTags, initials } from "../lib/tagMeta";
import * as store from "../lib/storage";

function socialLink(val, kind) {
  val = (val || "").trim();
  if (!val) return null;
  if (/^https?:\/\//i.test(val)) return val;
  const h = val.replace(/^@/, "");
  if (kind === "x") return `https://x.com/${h}`;
  if (kind === "linkedin") return h.includes("/") ? `https://${h}` : `https://linkedin.com/in/${h}`;
  if (kind === "github") return `https://github.com/${h}`;
  if (kind === "instagram") return `https://instagram.com/${h}`;
  return `https://${val}`;
}

function embedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return { type: "youtube", src: `https://www.youtube.com/embed/${yt[1]}` };
  const gd = url.match(/drive\.google\.com\/file\/d\/([\w-]+)/);
  if (gd) return { type: "drive", src: `https://drive.google.com/file/d/${gd[1]}/preview` };
  const loom = url.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return { type: "loom", src: `https://www.loom.com/embed/${loom[1]}` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "vimeo", src: `https://player.vimeo.com/video/${vm[1]}` };
  return null;
}

function LinkBtn({ href, Icon, children, testid }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className="flex items-center justify-center gap-2 rounded-pill border border-white/15 bg-white/5 px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-white/10">
      <Icon size={16} /> {children} <ExternalLink size={13} className="opacity-60" />
    </a>
  );
}

export default function ProjectPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toggleSave, isSaved } = useApp();
  const [project, setProject] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const [support, setSupport] = React.useState(false);
  const [supportTab, setSupportTab] = React.useState("pledge");
  const [all, setAll] = React.useState([]);

  React.useEffect(() => {
    window.scrollTo(0, 0);
    setProject(null); setNotFound(false);
    endpoints.project(slug).then((p) => {
      setProject(p);
      endpoints.event({ project_id: p.id, session_id: store.getSessionShuffle(), direction: "open" }).catch(() => {});
    }).catch(() => setNotFound(true));
  }, [slug]);

  React.useEffect(() => { endpoints.projects({ session_id: store.getSessionShuffle() }).then((d) => setAll(d.projects)).catch(() => {}); }, []);

  if (notFound) {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-10 text-center">
          <UBot size={96} />
          <h1 className="font-display text-[28px] font-bold">This project isn't available right now.</h1>
          <Link to="/deck" className="rounded-pill bg-gold-500 px-6 py-3 font-bold text-navy-900">Back to the deck</Link>
        </div>
      </Shell>
    );
  }
  if (!project) return <Shell><div className="flex flex-1 items-center justify-center text-on-dark-muted">Loading…</div></Shell>;

  const video = embedUrl(project.video_url);
  const tags = cardTags(project.u_tags);
  const team = (project.team || "").split(", ").filter(Boolean);
  const members = project.team_members || [];
  const memberNames = new Set(members.map((m) => (m.name || "").trim().toLowerCase()));
  const restTeam = team.filter((t) => !memberNames.has(t.trim().toLowerCase()));
  const nextProject = (() => {
    const i = all.findIndex((p) => p.slug === slug);
    if (i >= 0 && all.length > 1) return all[(i + 1) % all.length];
    return null;
  })();

  const share = async () => {
    const url = `${API}/share/${project.slug}`;
    const text = `Check out ${project.project} from U-Hackathon 2026: ${project.one_liner}`;
    try {
      if (navigator.share) await navigator.share({ title: project.project, text, url });
      else { await navigator.clipboard.writeText(url); toast("Link copied! Share it with your crew 🚀"); }
    } catch {}
  };

  const openSupport = (tab) => { setSupportTab(tab); setSupport(true); };
  const saved = isSaved(project.slug);
  const onSave = () => {
    const nowSaved = toggleSave(project);
    toast(nowSaved ? "Saved ⭐ Find it anytime in Saved." : "Removed from Saved");
  };

  return (
    <Shell>
      <div className="mx-auto w-full max-w-[760px] px-4 pb-28 pt-4">
        <button onClick={() => navigate(-1)} data-testid="project-back" className="mb-3 flex items-center gap-1.5 text-[14px] font-semibold text-on-dark-muted hover:text-white">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="overflow-hidden rounded-card" style={{ boxShadow: "0 24px 50px -18px rgba(6,15,28,0.80), 0 8px 20px -8px rgba(6,15,28,0.45)" }}>
          <HeroArt project={project} rounded="rounded-card" className="aspect-video w-full" />
        </div>

        <div className="mt-5">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <WinnerBadge slug={project.slug} link size="lg" />
            <TrackBadge track={project.track} />
            <span className="rounded-pill bg-white/10 px-2.5 py-1 text-[12px] font-bold text-white">{project.stage === "Live" ? "Live 🚀" : project.stage}</span>
          </div>
          <h1 className="font-display text-[34px] font-extrabold leading-tight text-white" data-testid="project-title">{project.project}</h1>
          <p className="mt-1 text-[15px] font-medium text-gold-400">Team {project.team}</p>
          {project.submitted_name && project.submitted_name !== project.project && (
            <p className="mt-1 text-[12px] text-ink-400">Submitted as "{project.submitted_name}"</p>
          )}
        </div>

        <Section title="The big news">
          <p className="text-[18px] font-medium leading-relaxed text-white/90">{project.press_release}</p>
        </Section>

        <Section title="Watch the demo">
          {video ? (
            <div className="overflow-hidden rounded-lg border border-white/10 bg-black">
              <iframe title="demo" src={video.src} className="aspect-video w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          ) : (
            <p className="rounded-lg border border-white/10 bg-white/5 p-4 text-on-dark-muted">{project.video_url ? "This video can't play here. Use the \"Watch the video\" button below." : "No demo video yet. Try the live demo instead!"}</p>
          )}
        </Section>

        <Section title="What it does">
          <p className="text-[16px] leading-relaxed text-white/85">{project.description}</p>
        </Section>

        {(project.demo_url || project.github_url || project.deck_url || project.video_url || project.website_url) && (
          <Section title="Try it yourself">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <LinkBtn href={project.demo_url} Icon={Rocket} testid="link-demo">Open live demo</LinkBtn>
              <LinkBtn href={project.github_url} Icon={Github} testid="link-github">View the code</LinkBtn>
              <LinkBtn href={project.deck_url} Icon={Presentation} testid="link-deck">See the pitch deck</LinkBtn>
              <LinkBtn href={project.website_url} Icon={Globe} testid="link-website">Visit the website</LinkBtn>
              {!video && <LinkBtn href={project.video_url} Icon={PlayCircle} testid="link-video">Watch the video</LinkBtn>}
            </div>
          </Section>
        )}

        <Section title="U-tags">
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => <TagChip key={t} tag={t} onClick={() => navigate(`/tags/${encodeURIComponent(t)}`)} />)}
          </div>
          <p className="mt-2 text-[12px] text-ink-400">Tap a tag to find similar projects</p>
        </Section>

        <Section title="Meet the team">
          {members.length > 0 && (
            <div className="mb-3 grid gap-2 sm:grid-cols-2" data-testid="team-profiles">
              {members.map((m, i) => (
                <div key={i} className="flex gap-3 rounded-xl bg-white/5 p-3 ring-1 ring-white/10">
                  <Avatar name={m.name} src={m.photo_url} size={48} className="shrink-0" />
                  <div className="min-w-0">
                    <p className="truncate font-display text-[15px] font-bold text-white">{m.name}{m.is_primary && <span className="ml-1.5 rounded-pill bg-gold-500/20 px-1.5 py-0.5 text-[10px] font-bold text-gold-400">Lead</span>}</p>
                    {m.bio && <p className="mt-0.5 line-clamp-2 text-[12px] text-on-dark-muted">{m.bio}</p>}
                    <div className="mt-1.5 flex items-center gap-2.5">
                      {socialLink(m.socials?.x, "x") && <a data-testid={`team-social-x-${i}`} href={socialLink(m.socials.x, "x")} target="_blank" rel="noopener noreferrer" className="text-on-dark-muted hover:text-white"><Twitter size={15} /></a>}
                      {socialLink(m.socials?.linkedin, "linkedin") && <a data-testid={`team-social-linkedin-${i}`} href={socialLink(m.socials.linkedin, "linkedin")} target="_blank" rel="noopener noreferrer" className="text-on-dark-muted hover:text-white"><Linkedin size={15} /></a>}
                      {socialLink(m.socials?.github, "github") && <a data-testid={`team-social-github-${i}`} href={socialLink(m.socials.github, "github")} target="_blank" rel="noopener noreferrer" className="text-on-dark-muted hover:text-white"><Github size={15} /></a>}
                      {socialLink(m.socials?.instagram, "instagram") && <a data-testid={`team-social-instagram-${i}`} href={socialLink(m.socials.instagram, "instagram")} target="_blank" rel="noopener noreferrer" className="text-on-dark-muted hover:text-white"><Instagram size={15} /></a>}
                      {socialLink(m.socials?.website, "website") && <a data-testid={`team-social-website-${i}`} href={socialLink(m.socials.website, "website")} target="_blank" rel="noopener noreferrer" className="text-on-dark-muted hover:text-white"><Globe size={15} /></a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {restTeam.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {restTeam.map((member) => (
                <div key={member} className="flex items-center gap-2 rounded-pill bg-white/5 py-1.5 pl-1.5 pr-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500 text-[12px] font-bold text-white">{initials(member)}</span>
                  <span className="text-[14px] font-medium text-white">{member}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <p className="mt-8 rounded-lg border border-white/10 bg-white/5 p-3 text-[12px] text-ink-400">
          This is an independent hackathon project. Some features may be prototypes. Please do your own research before relying on it.
        </p>

        <div className="mt-6 flex items-center justify-between">
          <button onClick={share} data-testid="project-share" className="flex items-center gap-2 rounded-pill border border-white/15 px-4 py-2 font-semibold text-white"><Share2 size={16} /> Share</button>
          {nextProject && (
            <button onClick={() => navigate(`/p/${nextProject.slug}`)} data-testid="project-next" className="flex items-center gap-2 rounded-pill px-4 py-2 font-semibold text-violet-300">Next project →</button>
          )}
        </div>
      </div>

      {/* sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 glass border-t border-white/10 px-4 py-3" data-testid="support-bar">
        <div className="mx-auto flex max-w-[760px] items-center gap-3">
          {SUPPORT_ENABLED ? (
            <>
              <button onClick={() => openSupport("message")} className="flex items-center gap-2 rounded-pill border border-white/20 px-4 py-3 font-semibold text-white">
                <MessageCircleHeart size={18} /> <span className="hidden sm:inline">Message the team</span>
              </button>
              <button onClick={() => openSupport("pledge")} data-testid="project-support-cta" className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-pill bg-gold-500 font-display text-[17px] font-bold text-navy-900">
                <HeartHandshake size={20} /> Support this project
              </button>
            </>
          ) : (
            <>
              <button onClick={share} data-testid="project-share-bar" className="flex items-center gap-2 rounded-pill border border-white/20 px-4 py-3 font-semibold text-white">
                <Share2 size={18} /> <span className="hidden sm:inline">Share</span>
              </button>
              <button onClick={onSave} data-testid="project-save-cta" className={`flex h-[52px] flex-1 items-center justify-center gap-2 rounded-pill font-display text-[17px] font-bold ${saved ? "bg-candy-500 text-white" : "bg-gold-500 text-navy-900"}`}>
                <Star size={20} className={saved ? "fill-white" : ""} /> {saved ? "Saved to favourites" : "Save to favourites"}
              </button>
            </>
          )}
        </div>
      </div>

      {SUPPORT_ENABLED && <SupportSheet project={project} open={support} onClose={() => setSupport(false)} defaultTab={supportTab} />}
    </Shell>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-7">
      <h3 className="mb-2 font-display text-[22px] font-bold text-white">{title}</h3>
      {children}
    </section>
  );
}
